import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { CalendarDays, Store, TrendingUp } from "lucide-react";
import LoadingSpinner from "@/components/LoadingSpinner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MESES = [
  "Janeiro", "Fevereiro", "Marco", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const fmtDia = (d) => {
  if (!d) return "-";
  const [ano, mes, dia] = d.split("-");
  return `${dia}/${mes}/${ano}`;
};

const toStr = (dt) => dt.toISOString().split("T")[0];

export default function DailySalesDialog({ loja, open, onOpenChange, fmt, dataInicio, dataFim, subtitulo }) {
  const [loading, setLoading] = useState(false);
  const [dias, setDias] = useState([]);
  const [totalMes, setTotalMes] = useState(0);

  const hoje = new Date();
  const rangeInicio = dataInicio || toStr(new Date(hoje.getFullYear(), hoje.getMonth(), 1));
  const rangeFim = dataFim || toStr(new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0));
  const legenda = subtitulo || `Vendas de ${MESES[hoje.getMonth()]} ${hoje.getFullYear()}`;

  const carregar = useCallback(async () => {
    if (!loja) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/dashboard/conferencia-mensal`, {
        params: { data_inicio: rangeInicio, data_fim: rangeFim, loja },
      });
      const listaDias = (res.data.dias || []).filter((d) => (d.total || 0) > 0);
      setDias(listaDias);
      setTotalMes(res.data.total_mes || 0);
    } catch {
      setDias([]);
      setTotalMes(0);
    } finally {
      setLoading(false);
    }
  }, [loja, rangeInicio, rangeFim]);

  useEffect(() => {
    if (open) carregar();
  }, [open, carregar]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden rounded-2xl border-0 shadow-[0_8px_30px_rgb(0,0,0,0.12)]" data-testid="daily-sales-dialog">
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-xl text-blue-600">
              <Store className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 leading-tight">{loja}</h2>
              <p className="text-sm text-slate-500 flex items-center gap-1">
                <CalendarDays className="w-3.5 h-3.5" /> {legenda}
              </p>
            </div>
          </div>
        </div>

        <div className="max-h-[60vh] overflow-y-auto px-6 py-2" data-testid="daily-sales-list">
          {loading ? (
            <div className="py-8"><LoadingSpinner message="Carregando vendas..." /></div>
          ) : dias.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-10">Nenhuma venda registrada neste periodo.</p>
          ) : (
            dias.map((d) => (
              <div key={d.data} className="flex justify-between items-center py-3 border-b border-slate-50 last:border-0" data-testid={`daily-sales-item-${d.data}`}>
                <span className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <CalendarDays className="w-4 h-4 text-slate-400" />
                  {fmtDia(d.data)}
                  {d.quantidade ? <span className="text-xs text-slate-400">· {d.quantidade} venda(s)</span> : null}
                </span>
                <span className="text-sm font-bold text-slate-900">{fmt(d.total)}</span>
              </div>
            ))
          )}
        </div>

        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-between items-center">
          <span className="text-xs font-medium uppercase tracking-wider text-slate-400 flex items-center gap-1">
            <TrendingUp className="w-4 h-4" /> Total do periodo
          </span>
          <span className="text-lg font-black tracking-tighter text-slate-900" data-testid="daily-sales-total">{fmt(totalMes)}</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
